import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const CACHE = "fhp-user";
const cached = () => { try { return JSON.parse(localStorage.getItem(CACHE)); } catch { return null; } };

export function AuthProvider({ children }) {
  const [user, setUserRaw] = useState(null);
  const setUser = useCallback((u) => {
    setUserRaw(u);
    try { if (u) localStorage.setItem(CACHE, JSON.stringify(u)); else if (u === false) localStorage.removeItem(CACHE); } catch { /* private mode */ }
  }, []);

  const check = useCallback(async () => {
    try { setUser((await api.me()) || false); } catch (e) {
      // No signal: keep the driver signed in from the last known account; recheck when back online.
      if (!e.response && cached()) { setUserRaw(cached()); return; }
      try { await api.refresh(); setUser((await api.me()) || false); } catch (e2) {
        if (!e2.response && cached()) setUserRaw(cached()); else setUser(false);
      }
    }
  }, [setUser]);

  useEffect(() => {
    // Returning from Google: AuthCallback exchanges the session first.
    if (window.location.hash?.includes("session_id=")) return;
    check();
    window.addEventListener("online", check);
    return () => window.removeEventListener("online", check);
  }, [check]);

  const signIn = async (email, password) => setUser(await api.login(email, password));
  const signUp = async (email, password, name, acceptTerms) => setUser(await api.register(email, password, name, acceptTerms));
  const signOut = async () => { try { await api.logout(); } finally { setUser(false); } };

  const google = (next = "/") => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + next;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <AuthContext.Provider value={{ user, setUser, signIn, signUp, signOut, google }}>{children}</AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
